import { SideEnum } from '@talos/kyoko';
import type { ManualFillCareOrderEntry } from './manualFillCareOrdersSlice';

interface ManualFillConfirmationMessageCopyability {
  canCopy: boolean;
  reason?: string;
}

/**
 * Builds the free-form confirmation text traders copy from the manual-fill dialog.
 *
 * The message is only supported for in-kind care orders entered in unit space, where each
 * underlying token leg is present in `LegSummary`. It uses the dialog's current fill
 * quantities so edits made before copying are reflected in the text.
 */
export function getManualFillConfirmationMessage(entry: ManualFillCareOrderEntry): string | undefined {
  if (!getManualFillConfirmationMessageCopyability(entry).canCopy) {
    return undefined;
  }

  const unitQty = entry.fillQty.value;
  const basketSymbol = entry.careOrder.basketOrderDetails?.BasketSymbol;
  const legsText = getConfirmationMessageLegsText(entry);
  if (!unitQty || !basketSymbol || !legsText) {
    // This is just for type-narrowing, since we already check these properties in the "copyability" function below
    return undefined;
  }

  const initiatingFrom = entry.careOrder.initiatingFrom ?? 'LP';
  const isSell = entry.careOrder.Side === SideEnum.Sell;
  const transferVerb = isSell ? 'receive' : 'send';
  const direction = isSell ? 'RD' : 'CR';

  return `For ${unitQty}u ${basketSymbol} ${direction}, can you please confirm ${initiatingFrom} will ${transferVerb} ${legsText}?`;
}

/**
 * Explains whether a manual-fill entry can produce confirmation text.
 *
 * The context menu uses this reason as the disabled tooltip, so keep these messages
 * user-facing and specific enough to tell the trader what data shape is unsupported.
 */
export function getManualFillConfirmationMessageCopyability(
  entry: ManualFillCareOrderEntry
): ManualFillConfirmationMessageCopyability {
  if (!entry.careOrder.isInKind) {
    return {
      canCopy: false,
      reason: 'Only in-kind care orders can have confirmation messages copied',
    };
  }

  if (!entry.careOrder.isQtyInUnits) {
    return {
      canCopy: false,
      reason: 'Only unit-space care orders can have confirmation messages copied',
    };
  }

  if (entry.careOrder.basketOrderDetails?.BasketSymbol == null) {
    return {
      canCopy: false,
      reason: 'Care order is missing a basket symbol',
    };
  }

  if (!entry.fillQty.value) {
    return {
      canCopy: false,
      reason: 'Care order is missing a fill quantity',
    };
  }

  if ((entry.legEntries?.length ?? 0) === 0) {
    return {
      canCopy: false,
      reason: 'Care order is missing underlying token leg details',
    };
  }

  if (entry.legEntries?.some(legEntry => !legEntry.fillQty.value) === true) {
    return {
      canCopy: false,
      reason: 'Care order has an underlying token leg missing a fill quantity',
    };
  }

  return { canCopy: true };
}

function getConfirmationMessageLegsText(entry: ManualFillCareOrderEntry): string | undefined {
  // Prefer editable dialog leg entries over original care-order data so copied text follows manual fill edits.
  const legs =
    entry.legEntries?.map(legEntry => ({
      fillQty: legEntry.fillQty.value,
      currency: legEntry.legSummary.Currency,
    })) ?? [];

  if (legs.length === 0 || legs.some(leg => !leg.fillQty)) {
    return undefined;
  }

  return legs.map(({ fillQty, currency }) => `${fillQty} ${currency}`).join(', ');
}
